/**
 * THE WMS MARKS -- one glyph pair, every surface.
 *
 * > Pete, 2026-09-27: *"We are also missing the WMS icon on the appointment
 * > with a circle slash showing no WMS (in lower right hand corner) and the WMS
 * > NO WMS search filters that follow Loading/Unloading next to status."*
 *
 * SPEC-appointment-wms-matching.md §7.2: *"ONE COMPONENT, LIKE `PauseMark`. The
 * tile, the party card, the panel row and the finder all draw the same mark."*
 *
 * ---- ⭐ IT JOINS THE PauseMark FAMILY, ON PURPOSE ------------------------
 *
 * Same `viewBox="0 0 48 48"`, same `r=19` ring, same wrapper contract, same
 * `currentColor`. A tile that swaps a pause bar for a WMS mark does not move a
 * single pixel of the text beside it.
 *   ** A STATE CHANGE THAT ALSO CHANGES THE LAYOUT IS TWO CHANGES, AND THE EYE
 *      ATTENDS TO THE WRONG ONE. ** (PauseMark.tsx, 2026-09-24)
 *
 * ---- ⛔⛔ AMBER, NEVER RED -------------------------------------------------
 *
 * ⚠️ THE CALLER SETS THE COLOUR, like every glyph in this family — but SPEC §7.2
 * is explicit about which colour, and the reason is not aesthetic:
 *
 * > *"IT MARKS AN ABSENCE, SO IT MUST NOT LOOK LIKE AN ERROR. Most appointments
 * > will carry it on the day they are booked and lose it later. Amber, not red
 * > — red is the no-show and the overdue, and a wall board full of red that
 * > means 'not linked yet' teaches people to ignore red."*
 *
 *   ** A WARNING SHOWN ON THE NORMAL CASE STOPS BEING A WARNING. **
 *
 * ---- ⚠️ THE LETTERS ARE NOT THE MESSAGE AT 16 PIXELS ---------------------
 *
 * On a Gantt chip this renders at `h-4 w-4`. Three letters inside a ring are
 * NOT legible at that size and are not meant to be: what reads is the SHAPE --
 * a ring with a bar through it, which is the universal "none of this". The
 * letters earn their place on the panel row and the finder, where the glyph is
 * larger and a reader wants to know *none of what*.
 *   ** A GLYPH THAT ONLY WORKS AT ONE SIZE IS A GLYPH THAT WILL BE DRAWN TWICE. **
 *
 * ⭐ And it is a SHAPE, not a hue, so it survives the board's configurable
 * palette (§1AV.261), a sun-bleached warehouse TV and a colour-blind loader.
 *
 * ---- ⛔ THREE STATES, AND `null` DRAWS NOTHING ---------------------------
 *
 * `AppointmentDto.hasWmsOrder` is `boolean | null`. `null` means the payload
 * never computed it -- `getAppointmentById` and the edit path build no matched
 * set. ⛔ NEITHER MARK MAY BE DRAWN ON `null`: an amber glyph on every
 * appointment fetched by a path that did not ask is worse than no glyph.
 *   ** NOT ASKED IS NOT THE SAME ANSWER AS NONE. **
 * That decision belongs to the caller and `wmsMarkFor()` below makes it once.
 */
import type { CSSProperties, ReactElement } from 'react';
import { cn } from '@/lib/utils';

interface Props {
    /** Class on the wrapper. ⚠️ The caller owns size AND colour -- amber. */
    wrapClassName: string;
    /**
     * Class on the svg itself. Kept separate from the wrapper for the same
     * reason `PauseMark` does: the board sizes glyphs off `--f-truck` on the
     * svg while the app sizes the wrapper in rem.
     */
    markClassName?: string;
    markStyle?: CSSProperties;
    /** Ring thickness. Small renderings need a heavier stroke to read at all. */
    ringStrokeWidth?: number;
    /** Hover text. Omit on the board, where nobody has a mouse. */
    title?: string;
}

/** ⚠️ SHARED BY BOTH MARKS so the two cannot drift apart by a pixel. */
function WmsLetters() {
    return (
        <text
            x="24" y="28.5"
            textAnchor="middle"
            /* ⚠️ fontSize in USER UNITS of the 48-box, not px -- it scales with
               whatever the caller sizes the svg to. 13 is the largest that
               leaves the ring's inner edge clear at r=19. */
            fontSize="13"
            fontWeight="700"
            letterSpacing="-0.5"
            fill="currentColor"
            /* ⛔ NOT the app font stack. A warehouse TV may not have it, and a
               fallback with different metrics would push the S past the ring. */
            fontFamily="ui-sans-serif, system-ui, sans-serif"
        >
            WMS
        </text>
    );
}

/** The appointment HAS a matching WMS order. Ring + letters, no slash. */
export function WmsMark({
    wrapClassName, markClassName, markStyle, ringStrokeWidth = 3.5, title,
}: Props): ReactElement {
    return (
        <span className={wrapClassName} title={title} aria-hidden="true">
            <svg className={markClassName} style={markStyle} viewBox="0 0 48 48">
                {/* ⚠️ r=19 MATCHES PauseMark AND WorkTruckMark EXACTLY. */}
                <circle cx="24" cy="24" r="19" fill="none" stroke="currentColor"
                        strokeWidth={ringStrokeWidth} />
                <WmsLetters />
            </svg>
        </span>
    );
}

/**
 * NO matching WMS order. Pete: *"a circle with a line thru it over the word
 * WMS"* -- so the slash goes OVER the letters, not beside them.
 */
export function NoWmsMark({
    wrapClassName, markClassName, markStyle, ringStrokeWidth = 3.5, title,
}: Props): ReactElement {
    return (
        <span className={wrapClassName} title={title} aria-hidden="true">
            <svg className={markClassName} style={markStyle} viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="19" fill="none" stroke="currentColor"
                        strokeWidth={ringStrokeWidth} />
                <WmsLetters />
                {/* ⭐ THE SLASH, AND IT IS THE PART THAT READS AT 16px.
                    ⚠️ Drawn LAST so it sits over the letters, and cut to the
                    ring's inner edge rather than crossing it -- a slash that
                    overshoots turns the ring into a lozenge at small sizes.
                    ⛔ A SECOND STROKE IN THE BACKGROUND COLOUR IS NOT USED: the
                    board's palette is configurable, so "the background" is not a
                    colour this component is allowed to know. */}
                <line x1="12.5" y1="35.5" x2="35.5" y2="12.5"
                      stroke="currentColor"
                      strokeWidth={ringStrokeWidth + 1}
                      strokeLinecap="round" />
            </svg>
        </span>
    );
}

/**
 * ⭐⭐ THE ONE PLACE THE THREE-STATE DECISION IS MADE.
 *
 * Every surface asks the same question and must give the same answer, so the
 * `null` rule lives here rather than in four call sites.
 *
 *   true  -> the positive mark, if the caller wants one
 *   false -> the circle-slash
 *   null  -> **nothing**, because the payload never asked
 *
 * ⚠️ `showWhenPresent` defaults FALSE. SPEC §7.2 asks for the ABSENCE mark; a
 * board where every linked truck also carries a glyph is a board of glyphs.
 */
export function wmsMarkFor(
    hasWmsOrder: boolean | null | undefined,
    props: Props,
    showWhenPresent = false,
): ReactElement | null {
    if (hasWmsOrder === null || hasWmsOrder === undefined) return null;
    if (hasWmsOrder) return showWhenPresent ? <WmsMark {...props} /> : null;
    return <NoWmsMark {...props} />;
}

/**
 * ============================================================================
 * ⭐⭐ THE TILE'S "NO WMS" BADGE (2026-09-28)
 * ============================================================================
 *
 * > Pete, with a screenshot: *"The no WMS icon looks wrong?"* → *"Thought it was the
 * > same color as warning, and size with WMS in the middle and slash thru it
 * > diagonally"*. Popup → *"NO WMS word pill, in the row (Recommended)"*.
 *
 * ⛔⛔ WHY THE RING HAD TO GO. `NoWmsMark` puts the letters inside a circle, and a
 * circle 16px across leaves the three letters about 4px tall — they rendered as
 * scribbles under the slash, so the mark read as a bare circle-slash. And it sat in
 * the tile's lower-right corner, where on a short tile it ran into the paperwork
 * badge above it.
 *   ** A WORD THAT CANNOT BE READ AT THE SIZE IT IS DRAWN IS DECORATION. **
 *
 * ⭐ SO: a TAG, not a ring. Its history on 2026-09-28: an amber 32×16 pill with bold
 * letters → 28×14, regular weight → (Pete, on the live board: *"Border around it needs
 * to be white. Lets remove the orange coloring and make it transparent so the blue
 * background comes thru. Text should be same font and style and size as IN and OUT
 * on the card"*) a WHITE-BORDERED, CLEAR tag set exactly like the IN / OUT tag,
 * with one white slash corner to corner. ⚠️ Not amber any more, by his choice — the
 * slash and the word carry the meaning; "WMS" in the middle at a
 * readable size, one diagonal slash through it. The slash is drawn twice — amber
 * under white — so where it crosses a letter there is a gap, not a smear.
 *
 * ⭐ WHERE IT SITS. Week and Month: IN THE BADGE ROW, after the licence badge — their
 * rows have the room. Day: THE LOWER-RIGHT CORNER, absolute (`className`).
 *   > Pete, 2026-09-28, after it was first moved into the Day title row: *"It cannot
 *   > fit on the top row. Has to go in the bottom right"*.
 * ⚠️ Why the corner now works when the ring did not: the ring was 20px tall (h-4 plus
 * p-0.5 padding) in a 36px chip, so from `bottom-0.5` it reached 4px into the title
 * row — that was the paperwork-badge collision. This pill is 16px, so from the same
 * corner it ends exactly where the title row begins.
 *   ** THE CORNER WAS NOT WRONG. THE MARK WAS 4px TOO TALL FOR IT. **
 *
 * ⚠️ `NoWmsMark` (the ring) stays for the filter chip's swatch, where it sits beside
 * the words "No WMS" and does not have to be read on its own.
 * ⛔ Only `false` draws — `null` means the answer was never computed.
 */
/**
 * ⛔ SUPPRESSED FOR NOW (F108, 2026-09-29). Pete: *"lets suppress the NO WMS button on
 * the appointment cards for now. We have the filter which should be good enough …
 * Want to see what that looks like with just having the NO WMS bubble up top."*
 * ⭐ ONE SWITCH, HERE, so Day, Week and Month all lose it together and it comes back
 * by flipping one word. The NO WMS FILTER (WmsFilterBar) and the header chip
 * (`NoWmsChip`) are unaffected.
 */
export const NO_WMS_TILE_BADGE_ENABLED = false;

export function NoWmsBadge({ hasWmsOrder, className }: {
    hasWmsOrder: boolean | null | undefined;
    /**
     * Extra classes, merged with `cn` (tailwind-merge), so a caller's value WINS a
     * conflict: the Day grid passes its corner position (`absolute …`, which replaces
     * the default `relative`), Week and Month pass `text-[8px] tracking-normal` to
     * match THEIR direction tags.
     */
    className?: string;
}): ReactElement | null {
    if (!NO_WMS_TILE_BADGE_ENABLED) return null;
    if (hasWmsOrder !== false) return null;
    return (
        <span
            className={cn(
                // ⭐ The Day tile's IN / OUT tag, letter for letter: rounded, border, px-1,
                // 9px semibold, tracking-wide, leading-tight — but WHITE on CLEAR, so the
                // tile's own colour shows through (Pete, 2026-09-28).
                'relative inline-block shrink-0 rounded border border-white bg-transparent px-1',
                'text-[9px] font-semibold tracking-wide leading-tight text-white',
                className,
            )}
            title="No matching WMS order"
            aria-label="No matching WMS order"
        >
            WMS
            {/* the slash: corner to corner, 1px, whatever the tag's width */}
            <svg
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <line
                    x1="4" y1="96" x2="96" y2="4"
                    stroke="#ffffff"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>
        </span>
    );
}
